import { appendFrcCameraToHref } from '~/utils/frameRateCalculator/frcCameraRoute';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';

export type FrameRateCalculatorPdpLink = {
  visible: boolean;
  href: string;
  label: string;
  tooltip: string;
};

export function useFrameRateCalculatorPdpLink(
  decoratedProduct: Ref<DecoratedProduct | null | undefined>
) {
  const { $textDictionary, $activeLocale, $shopStore } = useNuxtApp();

  const cameraName = computed(() => {
    const product = decoratedProduct.value;
    if (!product) {
      return '';
    }
    return (product.modelName ?? product.name ?? '').trim();
  });

  const shouldCheck = computed(() => {
    const product = decoratedProduct.value;
    return Boolean(product?.cameraCategory) && cameraName.value.length > 0;
  });

  const frcResponse = ref<{ isInFrc: boolean } | null>(null);

  watch(
    [
      shouldCheck,
      cameraName,
      () => $activeLocale.value.webapp,
      () => $shopStore.value
    ],
    async () => {
      if (!shouldCheck.value) {
        frcResponse.value = null;
        return;
      }
      try {
        frcResponse.value = await $fetch<{ isInFrc: boolean }>(
          '/api/tools/frc/isInFrc',
          {
            query: {
              name: cameraName.value,
              locale: $activeLocale.value.webapp,
              store: $shopStore.value
            }
          }
        );
      } catch {
        frcResponse.value = null;
      }
    },
    { immediate: true }
  );

  const link = computed((): FrameRateCalculatorPdpLink => {
    const fb = (key: string, fallback: string) =>
      $textDictionary.value?.[key] ?? fallback;

    const baseHref =
      fb(
        'pdp.frameRateCalculator.linkHref',
        '/en/tools/frame-rate-calculator'
      ) || fb('pdp.frc.linkhref', '/en/tools/frame-rate-calculator');

    const href = appendFrcCameraToHref(baseHref, cameraName.value);

    return {
      visible: shouldCheck.value && Boolean(frcResponse.value?.isInFrc),
      href,
      label: fb(
        'pdp.frameRateCalculator.linkText',
        fb('pdp.frc.linktext', 'Calculate Framerate')
      ),
      tooltip: fb(
        'pdp.frameRateCalculator.linkTooltip',
        fb('pdp.frc.link.tooltip', 'Open Frame Rate Calculator for this camera')
      )
    };
  });

  return { cameraName, link };
}
